import type {
  AdminGame,
  MetadataStatus,
  SgdbIdentifyRequest,
  SgdbIdentifyResponse,
  SgdbSearchResult,
} from '@mcgames/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '../../api/hooks.ts';

export const GAMES_PATH = '/api/admin/games';

export function gamePath(id: string): string {
  return `${GAMES_PATH}/${encodeURIComponent(id)}`;
}

const NZB_POLL_MS = 3000;

/** The editor refreshes the game every 3 s, and only while the NZB check of a version runs. */
export function nzbPollInterval(game: AdminGame | undefined): number | false {
  const checking = game?.versions.some((version) => version.nzb?.status === 'checking') ?? false;
  return checking ? NZB_POLL_MS : false;
}

export function useAdminGame(id: string) {
  return useApiQuery<AdminGame>(gamePath(id), {
    refetchInterval: (query) => nzbPollInterval(query.state.data),
  });
}

/** Which metadata providers the server has configured; they do not change while the page is open. */
export function useMetadataStatus() {
  return useApiQuery<MetadataStatus>('/api/admin/metadata/status', { staleTime: Infinity });
}

/**
 * A mutation on `<game>/<subpath>` that answers with the updated game. The response replaces the
 * editor's cached game, and the games list is refetched the next time it is shown.
 */
export function useGameMutation<TBody>(
  id: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  subpath = '',
) {
  const queryClient = useQueryClient();
  return useApiMutation<TBody, AdminGame>(method, `${gamePath(id)}${subpath}`, {
    onSuccess: (game) => {
      queryClient.setQueryData<AdminGame>([gamePath(id)], game);
      void queryClient.invalidateQueries({ queryKey: [GAMES_PATH] });
    },
  });
}

export function sgdbGamePath(sgdbId: number): string {
  return `/api/admin/metadata/steamgriddb/${sgdbId}`;
}

/** Name and year of the SteamGridDB game; a game's identity does not change behind the editor. */
export function useSgdbGame(sgdbId: number) {
  return useApiQuery<SgdbSearchResult>(sgdbGamePath(sgdbId), { staleTime: Infinity });
}

/** Caches a picked search result as the SteamGridDB game, so its name shows without a request. */
export function useRememberSgdbGame(): (result: SgdbSearchResult) => void {
  const queryClient = useQueryClient();
  return (result) => queryClient.setQueryData([sgdbGamePath(result.id)], result);
}

/** Identifies the game on SteamGridDB; the server imports the artwork and answers per kind. */
export function useSgdbIdentify(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<SgdbIdentifyRequest, SgdbIdentifyResponse>(
    'POST',
    `${gamePath(id)}/steamgriddb-identify`,
    {
      onSuccess: ({ game }) => {
        queryClient.setQueryData<AdminGame>([gamePath(id)], game);
        void queryClient.invalidateQueries({ queryKey: [GAMES_PATH] });
      },
    },
  );
}
